import { readFragment, ResultOf } from "gql.tada";
import { InsightsBlockFragment } from "../../../queries/fragments/InsightsBlockFragment";
import cx from "classnames";
import { useMemo } from "react";
import { Link } from "../../shared/core/Link";
import { LinkFragment } from "../../../queries/fragments/LinkFragment";
import { IconPlus } from "@hawkins/assets";

const variants = [
  "blank",
  "default",
  "2-col-default",
  "2-col-img",
  "3-col-title",
] as const;

const useIsValidVariant = (
  variant?: string | null
): variant is (typeof variants)[number] =>
  useMemo(
    () =>
      !!variant && variants.some((otherVariant) => otherVariant === variant),
    [variant]
  );

export const InsightsBlock = ({
  block,
}: {
  block: ResultOf<typeof InsightsBlockFragment> | null;
}) => {
  const variant = block?.variant;
  const isValid = useIsValidVariant(variant);

  if (!isValid || block == null) return null;

  const readMoreLink = readFragment(LinkFragment, block.readMoreLink);

  if (variant === "blank") return <div className="hidden dsk:block" />;

  if (variant === "3-col-title")
    return (
      <div className="dsk:col-span-3 px-4 dsk:px-8 py-24 dsk:py-64">
        <p className="text-center text-balance text-heading-1">
          {block.mainText}
        </p>
      </div>
    );

  return (
    <div
      className={cx(
        "py-5 px-4 dsk:p-8 bg-neutral-light/15 rounded-xl relative overflow-hidden flex flex-col w-full",
        variant === "2-col-img" ? "min-h-[636px]" : "min-h-[380px]",
        (variant === "2-col-default" || variant === "2-col-img") &&
          "dsk:col-span-2"
      )}
      style={{ alignSelf: "start" }}
    >
      {variant === "2-col-img" && block.backgroundImage?.url && (
        <img
          className="absolute inset-0 size-full object-cover brightness-60"
          src={block.backgroundImage.url}
        />
      )}
      {(block.icon || block.category) && (
        <div className="flex justify-between z-10">
          {(block.icon || block.category) && (
            <div className="flex items-center gap-3">
              {!!block.icon?.url && (
                <div className="p-2.5 rounded-lg flex items-center justify-center bg-brand-cream/15">
                  <img src={block.icon.url} />
                </div>
              )}
              {block.category && (
                <p className="text-button-large text-neutral-light">
                  {block.category}
                </p>
              )}
            </div>
          )}
          {block.extraInfoLabel && (
            <p className="text-heading-3">{block.extraInfoLabel}</p>
          )}
        </div>
      )}
      {!!block.shortHighlightedValue && (
        <p className="text-heading-1 xl:text-display text-neutral-light pb-4 z-10">
          {block.shortHighlightedValue}
        </p>
      )}
      <p
        className={cx(
          "z-10 grow",
          variant === "2-col-img"
            ? "uppercase text-caption text-neutral-light flex flex-col justify-end"
            : block.shortHighlightedValue
            ? "text-body-large"
            : "pt-12 xl:pt-20 text-heading-4"
        )}
      >
        {block.mainText}
      </p>
      <div
        className={cx(
          "flex flex-col gap-2 z-10 mt-5",
          variant === "2-col-img" && "text-brand-cream"
        )}
      >
        {!!block.bottomText && (
          <p className="text-button-large font-normal">{block.bottomText}</p>
        )}
        {readMoreLink && (
          <div className="flex flex-col justify-end items-left pt-4 z-10">
            <Link
              link={readMoreLink}
              className="text-button-large flex gap-2 items-center"
            >
              <span>{readMoreLink.text}</span>
              <IconPlus size="small" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
